import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FeedbackWidget, useUserhubAdmin, type FeedbackTriggerProps } from '@userhub/widget'
import { useLang } from '../i18n'
import { useTheme } from '../theme'
import { api } from '../lib/api'
import { navigate, usePath, desktopDeepLink, accountSettingsPath, appearanceSettingsPath, bidPath, corporateTemplatesPath, versionRetentionPath, feedbackTriagePath } from '../lib/router'
import { isDesktopApp } from '../lib/desktop'
import { THEMES } from '../lib/appearance-options'
import { HelpMenu } from './HelpMenu'
import { RegisterRating } from '../tours/rating'

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '·'
}

interface ActionItem {
  key: string
  label: string
  act: () => void
}

/**
 * The member's account row — initials / first name / role — opening one menu, top to bottom:
 *
 *  - the colour scheme, a `menuitemradio` group that does not close the menu on pick;
 *  - "Appearance settings" (the Settings page on Appearance, where text size lives),
 *    "AI settings" and — for a tenant admin only — "Corporate templates", "Version retention" and
 *    "Template library" (the hidden library bid, opened in the normal workspace), plain `menuitem` links;
 *  - the Feedback group: Report a bug, Request a feature, My tickets, — for a userhub admin only —
 *    "Feedback triage" (the /feedback-triage page), and Rate the app. The dialogs open through
 *    `feedback`, the actions `<FeedbackWidget renderTrigger>` hands this menu (see TopBar below). Niobe's own buttons and styling, not the widget's Fluent
 *    `FeedbackMenuItems`. Indented like the theme radios, so both labelled groups line up.
 *
 * Structurally ExportMenu.tsx: rootRef/triggerRef/itemsRef, Escape/outside-click close returning
 * focus to the trigger. EVERY item, radios and links alike, is in one roving-tabindex cycle in
 * visual order, so ArrowUp/ArrowDown/Home/End reach all of them (the WAI-ARIA menu pattern). A
 * link or a feedback entry closes the menu first, then acts.
 */
function AccountMenu({
  name,
  firstName,
  role,
  tenantAdmin,
  userhubAdmin,
  templateLibraryBidId,
  feedback,
}: {
  name: string
  firstName: string
  role: string
  /** Offers "Corporate templates" and "Version retention" — an affordance only: the pages and the backend check again. */
  tenantAdmin: boolean
  /** Offers "Feedback triage" — userhub's own admin allow-list (useUserhubAdmin), NOT tenantAdmin.
   *  An affordance only: the widget and the userhub API check again. */
  userhubAdmin: boolean
  /** The template library's bid id (GET /v1/me, tenant admins only) — offers "Template library". */
  templateLibraryBidId: string | null
  feedback: FeedbackTriggerProps
}) {
  const { t } = useLang()
  const { theme, setTheme } = useTheme()
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([])

  // Below the radios, in visual order.
  const links: ActionItem[] = [
    { key: 'appearance', label: t('appearance_settings_menu_item'), act: () => navigate(appearanceSettingsPath) },
    { key: 'ai', label: t('ai_settings_menu_item'), act: () => navigate(accountSettingsPath) },
    ...(tenantAdmin
      ? [
          { key: 'corporate-templates', label: t('corp_tpl_menu_item'), act: () => navigate(corporateTemplatesPath) },
          { key: 'version-retention', label: t('retention_menu_item'), act: () => navigate(versionRetentionPath) },
        ]
      : []),
    ...(tenantAdmin && templateLibraryBidId
      ? [{ key: 'template-library', label: t('template_library'), act: () => navigate(bidPath(templateLibraryBidId)) }]
      : []),
  ]
  const feedbackItems: ActionItem[] = [
    { key: 'bug', label: t('feedback_report_bug'), act: feedback.onReportBug },
    { key: 'feature', label: t('feedback_request_feature'), act: feedback.onRequestFeature },
    { key: 'tickets', label: t('feedback_my_tickets'), act: feedback.onOpenTickets },
    ...(userhubAdmin ? [{ key: 'triage', label: t('feedback_triage'), act: () => navigate(feedbackTriagePath) }] : []),
    { key: 'rate', label: t('feedback_rate_app'), act: feedback.onRate },
  ]
  const linksStart = THEMES.length
  const feedbackStart = linksStart + links.length
  const itemCount = feedbackStart + feedbackItems.length
  const pending = feedback.pendingCount

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (open) itemsRef.current[active]?.focus()
  }, [open, active])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  function onMenuKeyDown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        close(true)
        break
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => (i + 1) % itemCount)
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => (i - 1 + itemCount) % itemCount)
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(itemCount - 1)
        break
    }
  }

  function openMenu(): void {
    setActive(0)
    setOpen(true)
  }

  function actionButton(item: ActionItem, i: number, extra?: ReactNode, indent = false) {
    return (
      <button
        key={item.key}
        ref={(el) => { itemsRef.current[i] = el }}
        className={indent ? 'export-item indent' : 'export-item'}
        role="menuitem"
        tabIndex={active === i ? 0 : -1}
        onClick={() => { close(false); item.act() }}
      >
        {item.label}
        {extra}
      </button>
    )
  }

  // "My tickets (2)", and a screen reader hears why the number is there.
  const pendingSuffix =
    pending > 0 ? (
      <>
        {` (${pending})`}
        <span className="visually-hidden">
          {', ' + t('feedback_my_tickets_pending').replace('{n}', () => String(pending))}
        </span>
      </>
    ) : null

  return (
    <div className="account-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        className={`role${open ? ' on' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={name}
        onClick={() => (open ? close(false) : openMenu())}
      >
        <span className="ic" aria-hidden="true">{initials(name)}</span>
        <span className="who2"><b>{firstName}</b><small>{role}</small></span>
        <span className="chev" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div id={menuId} className="export-pop" role="menu" aria-label={t('settings_menu')} onKeyDown={onMenuKeyDown}>
          <div className="export-grp-lbl" aria-hidden="true">{t('theme_group_label')}</div>
          <div role="group" aria-label={t('theme_group_label')}>
            {THEMES.map((th, i) => (
              <button
                key={th.id}
                ref={(el) => { itemsRef.current[i] = el }}
                role="menuitemradio"
                aria-checked={theme === th.id}
                tabIndex={active === i ? 0 : -1}
                className={`export-item${theme === th.id ? ' checked' : ''}`}
                onClick={() => { setTheme(th.id); setActive(i) }}
              >
                {t(th.label)}
              </button>
            ))}
          </div>
          <div role="separator" className="export-sep" />
          {links.map((item, j) => actionButton(item, linksStart + j))}
          <div role="separator" className="export-sep" />
          <div className="export-grp-lbl" aria-hidden="true">{t('feedback_group_label')}</div>
          <div role="group" aria-label={t('feedback_group_label')}>
            {feedbackItems.map((item, j) =>
              actionButton(item, feedbackStart + j, item.key === 'tickets' ? pendingSuffix : null, true),
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** `onOpenGuidedTours`: the Help menu's "Guided tours" entry, once a tour is available. */
export function TopBar({ onOpenGuidedTours = () => {} }: { onOpenGuidedTours?: () => void } = {}) {
  const { t, lang, setLang } = useLang()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const name = me.data?.name ?? '…'
  // The backend sends givenName; fall back to the display name's first token.
  const firstName = me.data?.givenName || name.split(/\s+/)[0] || name
  const role = me.data?.roles?.[0] ?? t('role_sl')
  const tenantAdmin = me.data?.roles?.includes('admin') ?? false
  const templateLibraryBidId = me.data?.templateLibraryBidId ?? null
  // userhub's own admin allow-list — false until known and on any failure. Never tenantAdmin.
  const userhubAdmin = useUserhubAdmin()
  const path = usePath()

  return (
    <header className="topbar">
      {/* Corporate lockup: the Capgemini wordmark, a rule, then the product name. The Niobe mark
          itself is not repeated here — it stays the favicon (public/favicon.svg). The nav toggle
          lives in the breadcrumb bar instead, which only exists once a bid is open (see App). */}
      <div className="brand">
        <img className="cg-logo" src="/capgemini-logo.svg" alt="Capgemini" />
        <span className="brand-rule" aria-hidden="true" />
        <b>Niobe</b>
      </div>

      <div className="spacer" />

      {/* The browser's hand-off to the app: a plain link to niobe://<this path>, on a bid page only.
          Never an automatic redirect — that would prompt every browser user, app or no app. Hidden
          inside the app itself, where it would only reopen the page it is on. */}
      {!isDesktopApp() && path.startsWith('/bid/') && (
        <a className="open-desktop" href={desktopDeepLink(path)} title={t('open_in_desktop_hint')}>
          {t('open_in_desktop')}
        </a>
      )}

      <div className="lang" role="group" aria-label="Language">
        <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>FR</button>
      </div>

      <HelpMenu onOpenGuidedTours={onOpenGuidedTours} />

      {/* The account menu IS the userhub feedback widget's trigger: renderTrigger hands it the
          actions that open the widget's dialogs. No count on the account button — "My tickets (n)"
          inside the menu carries it. The provider is mounted once, in main.tsx (UserhubShell).
          RegisterRating hands the widget's onRate to a guided tour's congratulations card. */}
      <FeedbackWidget
        renderTrigger={(fb) => (
          <>
            <RegisterRating onRate={fb.onRate} />
            <AccountMenu name={name} firstName={firstName} role={role} tenantAdmin={tenantAdmin} userhubAdmin={userhubAdmin} templateLibraryBidId={templateLibraryBidId} feedback={fb} />
          </>
        )}
      />
    </header>
  )
}
